/**
 * Enhanced KPI card with trend indicator, sparkline, and anomaly coloring.
 * Shows a large value, label, optional percentage change, sparkline, and prev value.
 */

import { t } from "../i18n";
import * as shared from "../styles/shared";
import { SparklineChart } from "./charts/SparklineChart";

type TrendDirection = "up" | "down" | "flat";
type AnomalyLevel = "normal" | "warning" | "alert";

type Props = {
  readonly label: string;
  readonly value: string;
  readonly trend?: {
    readonly percent: number;
    readonly direction: TrendDirection;
  };
  readonly sparkline?: ReadonlyArray<number>;
  readonly anomalyLevel?: AnomalyLevel;
  readonly prevValue?: string;
  readonly sub?: string;
  readonly alert?: boolean;
  readonly onClick?: () => void;
};

const TREND_COLORS: Record<TrendDirection, string> = {
  up: "#16a34a",
  down: "#dc2626",
  flat: "#9ca3af",
};

const SPARKLINE_COLORS: Record<TrendDirection, string> = {
  up: "#16a34a",
  down: "#dc2626",
  flat: "#111111",
};

const ANOMALY_BG: Record<AnomalyLevel, string> = {
  normal: "#ffffff",
  warning: "#fef9c3",
  alert: "#fef2f2",
};

const ANOMALY_BORDER: Record<AnomalyLevel, string> = {
  normal: "var(--color-border)",
  warning: "#fde68a",
  alert: "#fecaca",
};

function formatTrend(direction: TrendDirection, percent: number): string {
  const arrow = t(`trend.${direction}`);
  return `${arrow} ${percent}%`;
}

function resolveAnomalyLevel(
  anomalyLevel: AnomalyLevel | undefined,
  alert: boolean | undefined,
): AnomalyLevel {
  if (anomalyLevel !== undefined) return anomalyLevel;
  if (alert) return "alert";
  return "normal";
}

export function TrendCard({
  label,
  value,
  trend,
  sparkline,
  anomalyLevel,
  prevValue,
  sub,
  alert,
  onClick,
}: Props): React.JSX.Element {
  const isClickable = typeof onClick === "function";
  const level = resolveAnomalyLevel(anomalyLevel, alert);
  const sparklineColor =
    trend !== undefined ? SPARKLINE_COLORS[trend.direction] : "#111111";

  return (
    <div
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        isClickable
          ? (e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") onClick();
            }
          : undefined
      }
      style={{
        ...shared.card,
        padding: 20,
        background: ANOMALY_BG[level],
        borderColor: ANOMALY_BORDER[level],
        cursor: isClickable ? "pointer" : "default",
        transition: "background 0.15s, box-shadow 0.15s",
      }}
    >
      <div style={s.headerRow}>
        <span style={s.label}>{label}</span>
        {trend !== undefined && (
          <span style={{ ...s.trend, color: TREND_COLORS[trend.direction] }}>
            {formatTrend(trend.direction, trend.percent)}
          </span>
        )}
      </div>
      <span style={s.value}>{value}</span>
      {sparkline !== undefined && sparkline.length > 0 && (
        <div style={s.sparklineWrapper}>
          <SparklineChart data={sparkline} color={sparklineColor} height={40} />
        </div>
      )}
      {prevValue !== undefined && (
        <span style={s.prevValue}>
          {t("dashboard.vs_last_week", { value: prevValue })}
        </span>
      )}
      {sub !== undefined && <span style={s.sub}>{sub}</span>}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  headerRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  label: {
    fontSize: 13,
    color: "var(--color-text-secondary)",
    fontWeight: 500,
  },
  value: {
    display: "block",
    fontSize: 32,
    fontWeight: 700,
    fontFamily: "var(--font-mono)",
    color: "var(--color-text)",
  },
  trend: {
    fontSize: 13,
    fontWeight: 600,
    flexShrink: 0,
  },
  sparklineWrapper: {
    marginTop: 8,
  },
  prevValue: {
    display: "block",
    fontSize: 11,
    fontWeight: 400,
    color: "var(--color-text-secondary)",
    marginTop: 4,
  },
  sub: {
    display: "block",
    fontSize: 12,
    color: "var(--color-text-secondary)",
    marginTop: 4,
  },
};
